// A box office is a per-EVENT point of sale. The real model (boxOfficeRoutes.ts
// `boxOfficeSchema`, and the organizer app's own form in ~/tkt-new-ui
// dashboard/events/[event]/box-offices) is exactly: name, email, phone, location,
// expiryDate, eventId, and the ticket types it may sell (selectedTicketTypes).
// The previous wizard invented scopes, cash/daily limits, printer models, ticket
// stock, credential delivery, bilingual toggles… none of which the backend stores,
// and it sent selectedTicketTypes:[] — so it never captured the one thing that
// matters: WHAT this box office sells. This is the honest form.
import React, { useState, useEffect, useCallback } from 'react';
import { Check, Loader2, Store } from 'lucide-react';
import { createBoxOffice, loadEventTicketTypes } from '../../lib/backOfficeApi';
import { toast } from '../../lib/buses';
import { fmtUGX } from '../../lib/format';
import { Field, Input } from '../../ui/Form';
import { Modal } from '../../ui/Modal';
import { EventSelect } from '../../components/filters/EventSelect';

const emailOk = (e) => /^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(String(e).trim());

export const CreateBoxOfficeWizardModal = ({ onClose, onCreate, presetEvent = null }) => {
  const [eventId, setEventId] = useState(presetEvent?.id || '');
  const [selectedEvent, setSelectedEvent] = useState(presetEvent); // the event picked (EventSelect searches; nothing preloaded)
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [phone, setPhone] = useState('');
  const [location, setLocation] = useState('');
  const [expiryDate, setExpiryDate] = useState('');
  const [submitting, setSubmitting] = useState(false);


  // The event's ticket types — the box office picks which it may sell + how many.
  const [tt, setTt] = useState({ status: 'idle', rows: [] });
  const [picked, setPicked] = useState({}); // { [ticketTypeId]: quantity }
  const loadTypes = useCallback((id, signal) => {
    if (!id) { setTt({ status: 'idle', rows: [] }); setPicked({}); return; }
    setTt({ status: 'loading', rows: [] });
    loadEventTicketTypes(id, { signal })
      .then((r) => {
        if (signal?.aborted) return;
        setTt({ status: 'ready', rows: r.tickets || [] });
        // default: include every type, allocation = its available inventory (min 1).
        const next = {};
        (r.tickets || []).forEach((t) => { next[t.id] = Math.max(1, Number(t.available) || Number(t.cap) || 50); });
        setPicked(next);
      })
      .catch((e) => { if (!signal?.aborted) setTt({ status: 'error', rows: [], error: e?.message }); });
  }, []);
  useEffect(() => { const c = new AbortController(); loadTypes(eventId, c.signal); return () => c.abort(); }, [eventId, loadTypes]);

  const toggle = (id) => setPicked((p) => {
    const next = { ...p };
    if (id in next) delete next[id];
    else { const t = tt.rows.find((x) => x.id === id); next[id] = Math.max(1, Number(t?.available) || 50); }
    return next;
  });
  const setQty = (id, v) => setPicked((p) => ({ ...p, [id]: Math.max(1, parseInt(v, 10) || 1) }));

  const selected = Object.entries(picked);
  const canSubmit = eventId && name.trim().length >= 3 && emailOk(email) && location.trim() && selected.length > 0;
  const submitReason = !eventId ? 'Pick the event' : name.trim().length < 3 ? (name.trim() ? 'Box office name needs at least 3 characters' : 'Enter the box office name') : !emailOk(email) ? 'Add a valid operator email' : !location.trim() ? 'Enter the location' : selected.length === 0 ? 'Pick at least one ticket type' : null;

  const submit = async () => {
    if (!canSubmit) return;
    setSubmitting(true);
    try {
      const created = await createBoxOffice({
        name: name.trim(),
        email: email.trim(),
        phone: phone.trim() || undefined,
        eventId,
        location: { name: location.trim() },
        expiryDate: expiryDate || undefined,
        selectedTicketTypes: selected.map(([id, quantity]) => ({ ticketType: { id }, quantity })),
      });
      const id = created?.id || created?.boxOffice?.id;
      if (onCreate) onCreate({ ...created, id, name });
      toast('Box office created', 'success', `${name} · ${selected.length} ticket type${selected.length === 1 ? '' : 's'}`);
      onClose();
    } catch (err) {
      toast('Box office not created', 'error', err?.message || 'Backend rejected the request');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Modal open={true} onClose={onClose} width="max-w-[560px]" title="Create box office"
      sub="A point of sale for one event. Staff sell the selected ticket types at the counter.">
      <div className="space-y-3.5">
        <Field label="Event" required>
          <EventSelect value={eventId} current={selectedEvent} onChange={(id, e) => { setEventId(id); setSelectedEvent(e || null); }} placeholder="Select the event this box office sells for…" />
        </Field>

        <div className="grid grid-cols-2 gap-3">
          <Field label="Box office name" required><Input value={name} onChange={(e) => setName(e.target.value)} placeholder="e.g. Main Gate · Counter 1" /></Field>
          <Field label="Location" required><Input value={location} onChange={(e) => setLocation(e.target.value)} placeholder="e.g. North entrance" /></Field>
        </div>
        <div className="grid grid-cols-2 gap-3">
          <Field label="Operator email" required sub="Sign-in + notices go here"><Input type="email" value={email} onChange={(e) => setEmail(e.target.value)} placeholder="operator@org.com" /></Field>
          <Field label="Phone" sub="Optional"><Input type="tel" value={phone} onChange={(e) => setPhone(e.target.value)} placeholder="+256 7XX XXX XXX" /></Field>
        </div>
        <Field label="Access expires" sub="Optional — leave blank for no expiry">
          <Input type="date" value={expiryDate} onChange={(e) => setExpiryDate(e.target.value)} />
        </Field>

        {/* Ticket types this box office may sell — the core of the record. */}
        <div>
          <div className="text-[12px] font-medium text-[#1F2937] mb-1.5">Ticket types it can sell {selected.length > 0 && <span className="text-[#9CA3AF] font-normal">· {selected.length} selected</span>}</div>
          <div className="border border-[#E6E4E1] rounded-md divide-y divide-[#EEEDEA] max-h-[220px] overflow-y-auto">
            {!eventId && <div className="px-3 py-6 text-center text-[12px] text-[#9CA3AF]">Pick an event to choose its ticket types.</div>}
            {eventId && tt.status === 'loading' && <div className="px-3 py-6 text-center text-[12px] text-[#6B7280]"><Loader2 className="w-3.5 h-3.5 animate-spin inline mr-1.5" />Loading ticket types…</div>}
            {eventId && tt.status === 'error' && <div className="px-3 py-6 text-center text-[12px] text-[#9B1F1F]">{tt.error || 'Could not load ticket types'}</div>}
            {eventId && tt.status === 'ready' && tt.rows.length === 0 && <div className="px-3 py-6 text-center text-[12px] text-[#9CA3AF]">This event has no ticket types yet.</div>}
            {eventId && tt.status === 'ready' && tt.rows.map((t) => {
              const on = t.id in picked;
              return (
                <div key={t.id} className="flex items-center gap-3 px-3 py-2">
                  <button type="button" onClick={() => toggle(t.id)} className={`w-4 h-4 rounded border flex items-center justify-center flex-shrink-0 ${on ? 'bg-[#1F2937] border-[#1F2937]' : 'border-[#C5C2BC]'}`}>{on && <Check className="w-3 h-3 text-white" />}</button>
                  <div className="min-w-0 flex-1">
                    <div className="text-[12.5px] font-medium text-[#1F2937] truncate">{t.tier}</div>
                    <div className="text-[10.5px] text-[#9CA3AF]">{fmtUGX(t.price)}{Number(t.available) ? ` · ${Number(t.available).toLocaleString()} available` : ''}</div>
                  </div>
                  {on && (
                    <label className="flex items-center gap-1.5 text-[11px] text-[#6B7280] flex-shrink-0">Qty
                      <input type="number" min={1} value={picked[t.id]} onChange={(e) => setQty(t.id, e.target.value)} className="w-20 h-7 px-2 text-[12px] rounded border border-[#E6E4E1] tabular-nums" />
                    </label>
                  )}
                </div>
              );
            })}
          </div>
        </div>
      </div>

      <div className="flex items-center justify-end gap-2 mt-5 pt-4 border-t border-[#EEEDEA]">
        {!canSubmit && submitReason && <span className="text-[11.5px] text-[#8A5A12]">{submitReason}</span>}
        <button onClick={onClose} className="h-8 px-3 rounded-md text-[12px] font-medium text-[#1F2937] hover:bg-[#F2F1ED]">Cancel</button>
        <button onClick={submit} disabled={!canSubmit || submitting} title={!canSubmit && submitReason ? submitReason : undefined} className="h-8 px-3.5 rounded-md text-[12px] font-medium bg-[#1F2937] hover:bg-[#111827] disabled:bg-[#C5C2BC] text-white inline-flex items-center gap-1.5">
          {submitting ? <><Loader2 className="w-3.5 h-3.5 animate-spin" />Creating…</> : <><Store className="w-3.5 h-3.5" />Create box office</>}
        </button>
      </div>
    </Modal>
  );
};
